import Button from "components/global/Button";
import Logo from "components/global/Logo";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  axiosInstance,
  getTooltip,
  getUser,
  getUserAccounts,
  resendVerificationEmail,
  setLoggedIn,
} from "api";
import { Redirect, useHistory } from "react-router-dom";
import { showAlert } from "utils/utils";
import { connect } from "react-redux";
import usePageTitle from "hooks/usePageTitle";
import { useLocation } from "react-router-dom";
import AlertBox from "components/global/AlertBox";

const Wrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);

  .contentContainer {
    width: 480px;
    gap: 48px;
    padding: 48px;
    border: solid #e2e2ea;
    border-radius: 8px;

    .pin-input {
      display: flex;
      justify-content: space-between;
      align-items: center;
      align-self: stretch;
      gap: 12px;
    }

    input {
      width: 56px;
      height: 72px;
      border-radius: 8px;
      border: solid #e2e2ea;
      padding: 23px 16px;
      font-size: 20px;
      font-weight: 450;
      line-height: 26px;

      &.error {
        border: 1px solid #fd0403;
      }
    }

    .buttonContainer {
      gap: 24px;

      .btn {
        width: 100%;
      }
    }
  }

  .footer {
    margin-top: 24px;
    font-size: 14px;
    font-style: normal;
    font-weight: 450;
    gap: 3px;
    justify-content: center;
    display: flex;

    a {
      font-size: 14px;
      font-style: normal;
      color: var(--secondary);
    }
  }

  @media screen and (max-width: 568px) {
    position: static;
    left: 0%;
    top: 0%;
    -webkit-transform: translate(0%, 0%);
    transform: translate(0%, 0%);

    .contentContainer {
      width: calc(100% - 24px);
      margin: 24px auto;

      input {
        width: 40px;
        height: 52px;
        padding: 13px 8px;
      }
    }
  }
`;

const VerifyEmailNew = ({ user, logged_in, user_token, refresh_token }) => {
  usePageTitle("Verify email");
  const { search } = useLocation();
  const postpaddyMobile = new URLSearchParams(search).get("postpaddy-mobile");
  const mobileUrl = localStorage.getItem("mobileUrl");
  const [otp, setOTP] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState(false);
  const [verify, setVerify] = useState(false);
  const [sending, setSending] = useState(false);
  const inputRefs = useRef([]);
  const router = useHistory();

  const handleInputChange = (index, value) => {
    const newOTP = [...otp];
    newOTP[index] = value;
    setOTP(newOTP);
    // Move to the next input box if current input is not empty
    if (value && index < otp.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("Text");
    const otpRegex = /\d{6}/;
    const extractedOTP = pastedData.match(otpRegex);
    if (extractedOTP) {
      const newOTP = extractedOTP[0].split("").slice(0, otp.length);
      setOTP(newOTP);
      // Move focus to the next input box
      inputRefs.current[newOTP.length]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && index > 0 && otp[index] === "") {
      // Move focus to the previous input box
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    setVerify(true);
    const code = otp.join("");

    try {
      const res = await axiosInstance.post("/user/verify", {
        code,
      });
      if (res.data.status) {
        await getUser();
        await getUserAccounts();
        await getTooltip();
        router.replace(`/onboarding/create-brand${search}`);

        setVerify(false);
      }
    } catch (error) {
      showAlert({
        msg:
          error?.response?.data?.message ??
          error?.message ??
          "An error occurred",
        type: "danger",
      });
      setError(true);
      setVerify(false);
    }
  };

  const handleResend = async () => {
    setSending(true);
    try {
      const res = await resendVerificationEmail({ email: user.email });
      if (res?.data?.status) {
        showAlert({
          msg: res?.data?.message ?? "Verification email sent successfully",
          type: "success",
        });
      } else {
        showAlert({
          msg: res?.data?.message ?? "An error occurred",
          type: "danger",
        });
      }
      setSending(false);
    } catch (e) {
      showAlert({
        msg: e.message ?? "An error occurred",
        type: "danger",
      });
      setSending(false);
    }
  };

  useEffect(() => {
    if (user?.email_verified_at && postpaddyMobile) {
      window.location.replace(
        `postpaddy.app://?token=${user_token}&refresh_token=${refresh_token}&success=true`
      );
      setLoggedIn(false);
      return;
    } else if (user?.email_verified_at) {
      router.push("/onboarding/create-brand");
    }
    // eslint-disable-next-line
  }, [user, postpaddyMobile]);

  useEffect(() => {
    getUser();
  }, []);

  if (!logged_in) {
    return (
      <Redirect
        to={`/sign-in?return_to=${encodeURIComponent(
          window.location.pathname + window.location.search
        )}`}
      />
    );
  }

  return (
    <>
      <AlertBox />
      <Wrapper>
        <div className="contentContainer flexColumn alignCenter">
          <Logo />
          <div className="flexColumn ">
            <h4 className="textCenter">Check your inbox</h4>
            <p className="l3 colorBodyText textCenter">
              We've sent a verification code via email to{" "}
              <span className="l3 bold">{user?.email}</span>, please enter this
              code to verify your account.
            </p>
          </div>
          <div className="buttonContainer flexColumn alignCenter justifyCenter spanFull">
            <div className="pin-input">
              {otp.map((value, index) => (
                <input
                  key={index}
                  placeholder=""
                  type="text"
                  name={`pin${index}`}
                  value={value}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  ref={(el) => (inputRefs.current[index] = el)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  maxLength={1}
                  onPaste={index === 0 ? handlePaste : null}
                  className={`textCenter ${error && "error"}`}
                  autoComplete="off"
                />
              ))}
            </div>
            <Button
              text="Verify email"
              bg="var(--secondary)"
              onClick={handleVerifyOtp}
              loading={verify}
              fullWidth
            />
          </div>
          <p className="l3 textNoWrap">
            Didn't receive the code?{" "}
            <button
              onClick={handleResend}
              disabled={sending}
              className="colorSecondary cursorPointer"
            >
              Resend code
            </button>
          </p>
        </div>
        <div className="footer">
          <p>Do you need help?</p>
          <a href="mailto:hello@postpaddy.com">Contact customer support</a>
        </div>
      </Wrapper>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user,
    logged_in: state.logged_in,
    user_token: state.user_token,
    refresh_token: state.refresh_token,
  };
};

export default connect(mapStateToProps)(VerifyEmailNew);
